import React from "react";
import type { PromptSource, PromptSourceReason } from "../../../services/visibilityService";

export const CONNECTIONS_PATH = "/social-posts?tab=connect";
const BUSINESS_SETTINGS_PATH = "/brand-details";

export interface PromptSourceNudge {
  title: string;
  body: string;
  /** Absent when there is nothing the owner can do about it. */
  cta?: string;
  href?: string;
}

/**
 * What to tell the owner about questions that are not their real searches
 * (SGL-178), or null when they are.
 *
 * Keyed on the reason, not just the source, because "connect Search Console"
 * is only right for someone who has not. The same generic set used to carry the
 * same sentence for a customer who never connected, one whose Google access had
 * expired, and one whose data we failed to read: the last two were being sent
 * to a flow that cannot help them.
 *
 * Only a missing connection (and an unknown reason on an older run) asks them
 * to connect. Everything else says what is actually wrong.
 */
export function promptSourceNudge(
  source: PromptSource | undefined,
  reason: PromptSourceReason | "" | null | undefined,
): PromptSourceNudge | null {
  if (source === "search_console") return null;

  const generated = source === "generated";
  const title = generated ? "Questions written from your services" : "Generic questions";
  const what = generated
    ? "We wrote these from the services on your profile, alongside a few standard ones. They are a good guess, not your real search data."
    : "These are our standard questions for your category, not your real search data.";

  switch (reason) {
    case "needs_reauth":
      return {
        title, cta: "Reconnect Search Console", href: CONNECTIONS_PATH,
        body: `${what} Search Console is connected, but Google has stopped giving us access. Reconnect it and the next check will use your real searches.`,
      };
    case "property_mismatch":
      return {
        title, cta: "Open Connections", href: CONNECTIONS_PATH,
        body: `${what} Search Console is connected, but not to this business's website. Connect the property for your site and the next check will use your real searches.`,
      };
    case "no_data":
      return {
        title,
        body: `${what} Search Console is connected but has no search data for your site yet. Once it does, the next check will use it.`,
      };
    case "too_few_queries":
      return {
        title,
        body: `${what} Search Console is connected but has too few searches for your site yet. Once it has more, the next check will use them.`,
      };
    case "no_website":
      return {
        title, cta: "Open business settings", href: BUSINESS_SETTINGS_PATH,
        body: `${what} Add your website to your business profile so we can match it to Search Console.`,
      };
    case "pull_failed":
    case "lookup_failed":
      return {
        title,
        body: `${what} Search Console is connected, but we could not read it for this check. That is a problem on our side and we have been alerted. The next check will try again.`,
      };
    case "not_connected":
    case "no_brand":
    case "":
    case null:
    case undefined:
      return {
        title, cta: "Connect Search Console", href: CONNECTIONS_PATH,
        body: `${what} Connect Google Search Console and we will measure what your buyers actually search.`,
      };
    default:
      // A reason this build does not know. Not "connect it": a newer reason is
      // far more likely to be a connected-but-failing case than a missing one.
      return { title, body: what };
  }
}

/**
 * Says where the questions came from, and when they are not the customer's real
 * searches, what to do about it. Storing `prompt_source` and not showing it
 * fixes nothing (SGL-178), so this is the half that matters.
 */
export default function PromptSourceNote({
  source, reason,
}: {
  source?: PromptSource;
  reason?: PromptSourceReason | "" | null;
}) {
  const nudge = promptSourceNudge(source, reason);
  if (!nudge) {
    return (
      <p className="avm-src avm-src-real">
        Built from the searches people actually used to find you, via Google Search Console.
      </p>
    );
  }
  return (
    <div className="avm-notice avm-notice-action avm-nudge" role="note">
      <div>
        <b>{nudge.title}</b>
        <p>{nudge.body}</p>
      </div>
      {nudge.cta && nudge.href && (
        <a className="avm-run avm-run-alt" href={nudge.href}>{nudge.cta}</a>
      )}
    </div>
  );
}
